import React, { useState, useEffect, useRef } from 'react';
import QueryResults from './QueryResults';
import { TimeRange, ConversationMessage, FeasibilityResponse, QueryResult } from '../../types/analytics';

interface ChatInterfaceProps {
  timeRange: TimeRange;
  apiBaseUrl: string;
  project: string;
  mode?: 'hive' | 'iceberg';
  timeTravelTimestamp?: string | null;
}

// Example questions - will be updated dynamically based on project
const PVQ_EXAMPLE_QUESTIONS = [
  "What's the average response time today?",
  "How many blocked requests in the last hour?",
  "Show user satisfaction by interaction type",
  "Which topics get blocked most often?",
  "What's the overall guardrail effectiveness?",
  "Show me interaction trends by hour",
  "How many interactions happened today?",
  "What percentage of requests were blocked?",
];

const PDT_EXAMPLE_QUESTIONS = [
  "How many designations were processed today?",
  "Show the breakdown of sensitivity designations",
  "What's the distribution of risk levels?",
  "Which agencies have the most designations?",
  "Show investigation tier distribution",
  "What are the most common form types?",
  "Show hourly designation trends",
  "What percentage are Critical-Sensitive?",
];

const ICEBERG_TIME_TRAVEL_HINTS = [
  "💡 Try: 'Show me data as it was 2 hours ago'",
  "💡 Try: 'What were the trends at 8pm yesterday?'",
  "💡 Iceberg mode supports querying historical snapshots!",
];

const ChatInterface: React.FC<ChatInterfaceProps> = ({ timeRange, apiBaseUrl, project, mode = 'hive', timeTravelTimestamp = null }) => {
  const EXAMPLE_QUESTIONS = project === 'pvq' ? PVQ_EXAMPLE_QUESTIONS : PDT_EXAMPLE_QUESTIONS;
  const [query, setQuery] = useState('');
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [showExamplesModal, setShowExamplesModal] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [selectedDatePreset, setSelectedDatePreset] = useState<'today' | 'yesterday' | 'last7' | 'last30' | 'other'>('today');
  const [naturalDateInput, setNaturalDateInput] = useState('');
  const [confirmedTimeRange, setConfirmedTimeRange] = useState<TimeRange>(timeRange);
  const [pendingQuery, setPendingQuery] = useState('');
  const conversationEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation, loading]);

  // Update confirmed time range when prop changes
  useEffect(() => {
    setConfirmedTimeRange(timeRange);
  }, [timeRange]);

  // Format date range for display
  const formatDateRange = (tr: TimeRange): string => {
    if (tr.startDate && tr.endDate) {
      return tr.startDate === tr.endDate
        ? tr.startDate
        : `${tr.startDate} to ${tr.endDate}`;
    }
    if (tr.year && tr.month && tr.day) {
      return `${tr.year}-${tr.month}-${tr.day}`;
    }
    return 'Not set';
  };

  // Format date range for message bubble display
  const formatDateRangeLabel = (tr: TimeRange): string => {
    if (tr.startDate && tr.endDate) {
      if (tr.startDate === tr.endDate) {
        return tr.startDate;
      }
      // Calculate days difference
      const start = new Date(tr.startDate);
      const end = new Date(tr.endDate);
      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 7) {
        return 'Last 7 Days';
      } else if (diffDays === 30) {
        return 'Last 30 Days';
      } else {
        return `${tr.startDate} to ${tr.endDate}`;
      }
    }
    if (tr.year && tr.month && tr.day) {
      return `${tr.year}-${tr.month}-${tr.day}`;
    }
    return 'Date range';
  };

  const getPresetDateRange = (preset: 'today' | 'yesterday' | 'last7' | 'last30'): TimeRange => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    switch (preset) {
      case 'today':
        return { startDate: todayStr, endDate: todayStr };
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = yesterday.toISOString().split('T')[0];
        return { startDate: yesterdayStr, endDate: yesterdayStr };
      case 'last7':
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return { startDate: sevenDaysAgo.toISOString().split('T')[0], endDate: todayStr };
      case 'last30':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        return { startDate: thirtyDaysAgo.toISOString().split('T')[0], endDate: todayStr };
    }
  };

  const handleDateModalSubmit = async () => {
    let finalTimeRange: TimeRange;

    if (selectedDatePreset === 'other') {
      // Use AI to interpret natural language date
      if (!naturalDateInput.trim()) {
        alert('Please enter a date range description');
        return;
      }

      try {
        // Call backend to interpret natural language date
        const response = await fetch(`${apiBaseUrl}/analytics/interpret-date`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ natural_date: naturalDateInput }),
        });

        if (!response.ok) {
          throw new Error('Failed to interpret date');
        }

        const dateData = await response.json();
        finalTimeRange = {
          startDate: dateData.start_date,
          endDate: dateData.end_date,
        };
      } catch (error) {
        alert('Could not interpret date range. Please try again or use a preset.');
        return;
      }
    } else {
      finalTimeRange = getPresetDateRange(selectedDatePreset);
    }

    console.log('Date modal submit - selected preset:', selectedDatePreset);
    console.log('Date modal submit - finalTimeRange:', finalTimeRange);

    setConfirmedTimeRange(finalTimeRange);
    setShowDateModal(false);

    // Now execute the pending query
    executeQuery(pendingQuery, finalTimeRange);
  };

  const handleSubmit = async (e?: React.FormEvent, forceExecute: boolean = false) => {
    e?.preventDefault();
    if (!query.trim() || loading) return;

    // If forceExecute is true, skip modal and execute directly
    if (forceExecute) {
      executeQuery(query, confirmedTimeRange);
      return;
    }

    // Always show date modal to allow user to confirm/change date range
    setPendingQuery(query);
    setShowDateModal(true);
  };

  const executeQuery = async (queryText: string, timeRangeToUse: TimeRange) => {
    console.log('executeQuery called with timeRangeToUse:', timeRangeToUse);

    const userMessage: ConversationMessage = {
      role: 'user',
      content: queryText,
      timeRange: timeRangeToUse,
    };

    setConversation((prev) => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    try {
      // Step 1: Assess feasibility
      const assessResponse = await fetch(`${apiBaseUrl}/analytics/assess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: queryText, project }),
      });

      const assessData: FeasibilityResponse = await assessResponse.json();

      if (!assessData.feasible) {
        setConversation((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `I cannot answer that query. ${assessData.reasoning}`,
            error: assessData.alternative_suggestion,
          },
        ]);
        setLoading(false);
        return;
      }

      // Step 2: Execute query using confirmed time range
      const queryPayload: any = { query: queryText, time_range: timeRangeToUse, project, mode };

      // Add time travel timestamp if provided (Iceberg mode only)
      if (timeTravelTimestamp && mode === 'iceberg') {
        // Convert local time to UTC for Iceberg time travel
        const localDate = new Date(timeTravelTimestamp);
        const utcTimestamp = localDate.toISOString().replace('Z', '').replace('T', 'T');
        queryPayload.as_of_timestamp = utcTimestamp;
      }

      console.log('Sending query request with payload:', queryPayload);

      const queryResponse = await fetch(`${apiBaseUrl}/analytics/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(queryPayload),
      });

      const queryData: QueryResult = await queryResponse.json();

      setConversation((prev) => [
        ...prev,
        {
          role: 'assistant',
          sql: queryData.sql,
          results: queryData.results,
          explanation: queryData.explanation,
          chart_type: queryData.chart_type,
        },
      ]);
    } catch (error) {
      setConversation((prev) => [
        ...prev,
        {
          role: 'assistant',
          error: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (exampleQuery: string) => {
    setQuery(exampleQuery);
    setShowExamplesModal(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      <div className="flex flex-col bg-gray-800 border border-gray-700 rounded-lg">
        {/* Conversation Area - Natural scrolling */}
        <div className="p-6 space-y-6 min-h-[400px]">
          {conversation.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full space-y-6">
              <div className="text-center space-y-3">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-900 bg-opacity-30 rounded-full">
                  <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-medium text-gray-100">Ask Questions About Your Data</h3>
                <p className="text-gray-400 max-w-md">
                  Use natural language to explore your analytics data. Type your question below or browse example questions.
                </p>
              </div>

              <button
                onClick={() => setShowExamplesModal(true)}
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                View Example Questions
              </button>

              <div className="bg-gray-700 bg-opacity-50 border border-gray-600 rounded-lg p-4 max-w-2xl">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-300">Tips for better results:</p>
                    <ul className="text-xs text-gray-400 space-y-1">
                      <li>• Be specific about what you want to know</li>
                      <li>• Ask for counts, averages, trends, or comparisons</li>
                      <li>• Reference relevant data fields and metrics</li>
                      <li>• Use natural language - no SQL knowledge needed</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {conversation.map((message, index) => (
                <div key={index}>
                  {message.role === 'user' ? (
                    <div className="flex items-start gap-3 justify-end">
                      <div className="bg-blue-600 rounded-2xl rounded-tr-sm px-5 py-3 max-w-2xl">
                        <p className="text-white">{message.content}</p>
                        {message.timeRange && (
                          <div className="mt-2 flex items-center gap-1.5">
                            <svg className="w-3.5 h-3.5 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs text-blue-100">{formatDateRangeLabel(message.timeRange)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <div className="flex-1 max-w-4xl">
                        {message.error ? (
                          <div className="bg-red-900 bg-opacity-30 border border-red-700 rounded-lg p-4">
                            <p className="text-red-300 font-medium mb-2">Unable to process query</p>
                            <p className="text-red-200 text-sm">{message.error}</p>
                          </div>
                        ) : (
                          <QueryResults
                            result={{
                              sql: message.sql || '',
                              results: message.results || [],
                              explanation: message.explanation || '',
                              chart_type: (message.chart_type as any) || 'table',
                              row_count: message.results?.length || 0,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                  <div className="bg-gray-700 rounded-2xl rounded-tl-sm px-5 py-4 max-w-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                      <span className="text-sm text-gray-400 ml-2">Analyzing query...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={conversationEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="border-t border-gray-700 p-4 bg-gray-800">
          {/* Quick Suggestion Chips (show when no conversation) */}
          {conversation.length === 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {EXAMPLE_QUESTIONS.slice(0, 3).map((example, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setQuery(example);
                    textareaRef.current?.focus();
                  }}
                  className="inline-flex items-center gap-1 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300 hover:text-gray-100 px-3 py-1.5 rounded-full text-sm transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {example.length > 50 ? example.substring(0, 50) + '...' : example}
                </button>
              ))}
            </div>
          )}

          <form onSubmit={(e) => handleSubmit(e)} className="flex flex-col gap-2">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question about your analytics data... (Press Enter to send, Shift+Enter for new line)"
                rows={2}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 pr-36 text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                disabled={loading}
              />
              <div className="absolute right-2 bottom-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowExamplesModal(true)}
                  className="bg-gray-600 hover:bg-gray-500 text-white px-3 py-2 rounded-lg font-medium transition-colors flex items-center gap-1"
                  title="View example questions"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </button>
                <button
                  type="submit"
                  disabled={loading || !query.trim()}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                      </svg>
                      Send
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>

          {/* Subtle date range indicator */}
          <div className="mt-2 text-xs text-gray-500 text-center">
            Active date range: {formatDateRange(confirmedTimeRange)}
          </div>
        </div>
      </div>

      {/* Date Range Selection Modal */}
      {showDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-2xl w-full">
            <div className="border-b border-gray-700 p-6">
              <h2 className="text-2xl font-bold text-gray-100">Select Date Range for Query</h2>
              <p className="text-gray-400 mt-1">
                Current range: <span className="text-blue-400 font-medium">{formatDateRange(confirmedTimeRange)}</span>
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Quick Presets */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">Quick Options:</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { value: 'today', label: 'Today' },
                    { value: 'yesterday', label: 'Yesterday' },
                    { value: 'last7', label: 'Last 7 Days' },
                    { value: 'last30', label: 'Last 30 Days' },
                  ].map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => setSelectedDatePreset(preset.value as any)}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        selectedDatePreset === preset.value
                          ? 'bg-blue-600 border-blue-500 text-white'
                          : 'bg-gray-700 border-gray-600 text-gray-300 hover:border-blue-500'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Other Option */}
              <div>
                <label className="flex items-center gap-2 mb-3">
                  <input
                    type="radio"
                    checked={selectedDatePreset === 'other'}
                    onChange={() => setSelectedDatePreset('other')}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-300">Other - Describe in natural language:</span>
                </label>

                {selectedDatePreset === 'other' && (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={naturalDateInput}
                      onChange={(e) => setNaturalDateInput(e.target.value)}
                      placeholder='e.g., "last week", "March 1 to March 10", "first two weeks of February"'
                      className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoFocus
                    />
                    <p className="text-xs text-gray-500">
                      AI will interpret your date description and convert it to a specific range
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-700 p-6 flex items-center justify-between">
              <button
                onClick={() => {
                  setShowDateModal(false);
                  setPendingQuery('');
                }}
                className="text-gray-400 hover:text-gray-200 transition-colors px-4 py-2"
              >
                Cancel
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    setShowDateModal(false);
                    executeQuery(pendingQuery, confirmedTimeRange);
                  }}
                  className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2.5 rounded-lg font-medium transition-colors"
                >
                  Use Current Range
                </button>
                <button
                  onClick={handleDateModalSubmit}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                  </svg>
                  Submit with Selected Range
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Example Questions Modal */}
      {showExamplesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-100">Example Questions</h2>
                <p className="text-gray-400 mt-1">Click any question to use it</p>
              </div>
              <button
                onClick={() => setShowExamplesModal(false)}
                className="text-gray-400 hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {EXAMPLE_QUESTIONS.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => handleExampleClick(example)}
                    className="group text-left bg-gray-700 hover:bg-gray-600 border border-gray-600 hover:border-blue-500 rounded-lg p-4 transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <span className="text-sm text-gray-300 group-hover:text-gray-100">{example}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatInterface;
